'use client';

import { HTMLProps, memo, useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { Button } from 'app/shared/buttons/Button';
import { Icons } from 'app/constants/icons';
import { useIsMobile } from 'app/hooks/useIsMobile';
import { cn } from 'app/utils/styles.utils';

export const _POSITIONS = [
  'left-center',
  'right-center',
  'top-center',
  'bottom-center',
  'bottom-left',
  'bottom-right',
  'top-left',
  'top-right',
] as const;

declare module 'react' {
  // eslint-disable-next-line
  interface HTMLAttributes<T> {
    'data-tooltip-target'?: string;
  }
}

export type Position = (typeof _POSITIONS)[number];

type TooltipProps = Omit<HTMLProps<HTMLDivElement>, 'target'> & {
  position?: Position;
  delay?: number;
  dismissOnPointerDown?: boolean;
};

const TARGET_PADDING = 5;

export const Tooltip = memo(
  ({dismissOnPointerDown = true, ...props}: TooltipProps) => {
    const {id, delay} = props;

    const [preTarget, setPreTarget] = useState<HTMLElement | null>(null!);
    const [target, setTarget] = useState<HTMLElement | null>(null!);

    const lastStateRef = useRef<HTMLElement | null>(null);
    const isMobile = useIsMobile();

    const hide = useCallback(() => {
      setPreTarget(null);
      setTarget(null);

      lastStateRef.current = null;
    }, []);

    // handle data props
    useEffect(() => {
      if (!id) return;

      const handleEvent = (event: Event) => {
        if (isMobile && event.type !== 'pointerdown') {
          return;
        }

        const el = event.target as HTMLElement;
        const targetContainer = el?.closest(
          `[data-tooltip-target="${id}"]`,
        ) as HTMLElement;
        const newState =
          targetContainer?.dataset.tooltipTarget === id
            ? targetContainer
            : null;

        const tooltip = document.querySelector('[data-tooltip-base]');
        const isClickInsideTooltip = tooltip?.contains(el);

        if (isMobile && isClickInsideTooltip) {
          return;
        }

        if (newState === lastStateRef.current) {
          if (isMobile && newState) {
            lastStateRef.current = null;

            setPreTarget(null);
          }

          return;
        }

        lastStateRef.current = newState;

        setPreTarget(newState);
      };

      document.addEventListener('focusin', handleEvent);
      document.addEventListener('pointermove', handleEvent);

      if (dismissOnPointerDown) {
        document.addEventListener('pointerdown', handleEvent);
      }

      return () => {
        document.removeEventListener('focusin', handleEvent);
        document.removeEventListener('pointermove', handleEvent);

        if (dismissOnPointerDown) {
          document.removeEventListener('pointerdown', handleEvent);
        }
      };
    }, [id, dismissOnPointerDown, isMobile]);

    // handle delay
    useEffect(() => {
      if (!preTarget) return;

      const resolvedDelay = isMobile ? 0 : delay ?? 0;

      const timeoutId = setTimeout(() => {
        setTarget(preTarget);
      }, resolvedDelay);

      return () => {
        clearTimeout(timeoutId);
        setTarget(null);

        if (isMobile) {
          document.body.style.overflow =
            document.body.dataset.prevOverflow ?? 'scroll';
        }
      };
    }, [preTarget, delay, isMobile]);

    if (!target) return null;

    return createPortal(
      <TooltipRender {...props} target={target} hide={hide} />,
      document.body,
    );
  },
);

Tooltip.displayName = 'Tooltip';

type TooltipRenderProps = TooltipProps & {
  target: HTMLElement;
  hide: () => void;
};

const TooltipRender = ({
  id,
  position = 'top-center',
  target,
  className,
  children,
  hide,
  ...props
}: TooltipRenderProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null!);
  const isMobile = useIsMobile();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const tooltip = tooltipRef.current;

    if (isMobile) {
      const prevOverflow = document.body.style.overflow;

      if (prevOverflow !== 'hidden') {
        document.body.dataset.prevOverflow = prevOverflow;
      }

      document.body.style.overflow = 'hidden';

      setIsOpen(true);

      return;
    }

    const updatePosition = () => {
      const coords = calculatePosition(tooltip, target, position);

      tooltip.style.left = `max(0px,${coords.x}px)`;
      tooltip.style.top = `max(0px, ${coords.y}px)`;
    };

    const handleEvent = (event?: Event) => {
      updatePosition();

      // check if cursor position is inside target

      if (!event || !('clientX' in (event as PointerEvent))) return;

      const pointerEvent = event as PointerEvent;
      const isFound = isPointInsideElement(
        target,
        pointerEvent.clientX,
        pointerEvent.clientY,
      );

      if (isFound) return;

      hide();
    };

    document.addEventListener('resize', handleEvent);
    document.addEventListener('pointermove', handleEvent);
    document.addEventListener('wheel', handleEvent);
    document.addEventListener('scroll', handleEvent);

    updatePosition();
    setIsOpen(true);

    return () => {
      document.removeEventListener('resize', handleEvent);
      document.removeEventListener('pointermove', handleEvent);
      document.removeEventListener('scroll', handleEvent);
      document.removeEventListener('wheel', handleEvent);
    };
  }, [id, position, target, hide, isMobile]);

  const render = () => {
    return (
      <div
        ref={tooltipRef}
        data-tooltip-base
        role="tooltip"
        className={cn(
          'fixed z-[300] flex max-w-[250px] select-none flex-col rounded-lg bg-charcoal-800 p-3 text-xs font-normal leading-4.5 text-neutral-100',
          {
            'opacity-0': !isOpen,
            'opacity-100': isOpen,
          },
          'mobile:relative mobile:w-full mobile:max-w-none',
          className,
        )}
        {...props}>
        {children}

        {isMobile && (
          <Button
            className="-mb-3 -ml-1 mt-5 flex h-14 items-center justify-center gap-1.5 border-t-1.5 border-charcoal-600 text-md text-white active:opacity-80 light:text-black"
            onClick={hide}>
            <Icons.close size={18} strokeWidth={2.5} />

            <span>Close</span>
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobile && (
        <div
          className={cn(
            'fixed left-0 top-0 flex items-center justify-center p-5',
            'z-[300] h-[100svh] w-[100vw] bg-[rgba(0,0,0,0.4)] backdrop-blur-[4px]',
          )}>
          {render()}
        </div>
      )}

      {!isMobile && render()}
    </>
  );
};

const calculatePosition = (
  tooltip: HTMLElement,
  target: HTMLElement,
  position: Position,
): {x: number; y: number} => {
  const tooltipRect = tooltip.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const positions = {
    'top-center': {
      x: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
      y: targetRect.top - tooltipRect.height - TARGET_PADDING,
    },
    'bottom-center': {
      x: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
      y: targetRect.bottom + TARGET_PADDING,
    },
    'left-center': {
      x: targetRect.left - tooltipRect.width - TARGET_PADDING,
      y: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
    },
    'right-center': {
      x: targetRect.right + TARGET_PADDING,
      y: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
    },
    'bottom-left': {
      x: targetRect.left,
      y: targetRect.bottom + TARGET_PADDING,
    },
    'bottom-right': {
      x: targetRect.right - tooltipRect.width,
      y: targetRect.bottom + TARGET_PADDING,
    },
    'top-left': {
      x: targetRect.left,
      y: targetRect.top - tooltipRect.height - TARGET_PADDING,
    },
    'top-right': {
      x: targetRect.right - tooltipRect.width,
      y: targetRect.top - tooltipRect.height - TARGET_PADDING,
    },
  };
  const coords = positions[position];

  // Ensure the tooltip doesn't go outside the viewport
  let adjustedX = coords.x;
  let adjustedY = coords.y;

  if (coords.x < 0) {
    adjustedX = TARGET_PADDING;
  } else if (coords.x + tooltip.offsetWidth > window.innerWidth) {
    adjustedX = window.innerWidth - tooltip.offsetWidth - TARGET_PADDING;
  }

  if (coords.y < 0) {
    adjustedY = TARGET_PADDING;
  } else if (coords.y + tooltip.offsetHeight > window.innerHeight) {
    adjustedY = window.innerHeight - tooltip.offsetHeight - TARGET_PADDING;
  }

  coords.x = adjustedX;
  coords.y = adjustedY;

  return coords;
};

const isPointInsideElement = (
  element: HTMLElement,
  x: number,
  y: number,
): boolean => {
  const rect = element.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};
